import React from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { PlatformPressable } from '@react-navigation/elements';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { appTheme, useThemeColors, useThemeStore } from '../../shared/theme';
import { androidFlatSurfaceStyle } from '../../shared/theme/sharedStyles';
import {
  ANDROID_FLOATING_TAB_BAR_HEIGHT,
} from './androidFloatingTabBarLayout';
import { MAIN_TAB_STYLES } from '../../shared/navigation/tabRegistry';

const ANDROID_TAB_BAR_LIGHT_BG = '#FFFFFF';

/** Web: RN `Pressable` is more reliable than `PlatformPressable` for tab switches. */
const TabPressable = Platform.OS === 'web' ? Pressable : PlatformPressable;

/** Android-only solid bottom tab bar (iOS uses native tabs). Web reuses this bar. */
export function AndroidFloatingTabBar({ state, descriptors, navigation }: BottomTabBarProps) {
  const insets = useSafeAreaInsets();
  const colors = useThemeColors() ?? appTheme.colors;
  const appearance = useThemeStore(s => s.appearance);
  const is_dark = appearance === 'dark';
  const label_font_size = MAIN_TAB_STYLES.android.labelFontSize;
  const tab_bar_background = is_dark ? colors.surface : ANDROID_TAB_BAR_LIGHT_BG;
  const tab_bar_border = is_dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';

  return (
    <View
      pointerEvents="box-none"
      style={[
        styles.host,
        androidFlatSurfaceStyle(),
        {
          paddingBottom: insets.bottom,
          backgroundColor: tab_bar_background,
          borderTopColor: tab_bar_border,
        },
      ]}>
      <View style={[styles.bar, { height: ANDROID_FLOATING_TAB_BAR_HEIGHT }]}>
        <View style={styles.row}>
          {state.routes.map((route, index) => {
            const { options } = descriptors[route.key];
            const focused = state.index === index;
            const active_color = options.tabBarActiveTintColor ?? colors.primary;
            const inactive_color = options.tabBarInactiveTintColor ?? MAIN_TAB_STYLES.android.inactiveTintColor;
            const color = focused ? active_color : inactive_color;
            const label = options.tabBarLabel ?? options.title ?? route.name;
            const icon = options.tabBarIcon?.({ focused, color, size: 22 });

            return (
              <TabPressable
                key={route.key}
                accessibilityRole="button"
                accessibilityState={focused ? { selected: true } : {}}
                accessibilityLabel={options.tabBarAccessibilityLabel}
                onPress={() => {
                  if (Platform.OS === 'web') {
                    // Avoid tabPress preventDefault quirks on RN-web; jump directly.
                    if (!focused) {
                      navigation.navigate(route.name, route.params);
                    }
                    return;
                  }
                  const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });
                  if (!focused && !event.defaultPrevented) {
                    navigation.navigate(route.name, route.params);
                  }
                }}
                onLongPress={() => navigation.emit({ type: 'tabLongPress', target: route.key })}
                style={styles.tab}>
                <View style={styles.icon_slot}>{icon}</View>
                <Text
                  style={[
                    styles.label,
                    { color, fontSize: label_font_size },
                    focused ? styles.label_focused : null,
                  ]}
                  numberOfLines={1}>
                  {typeof label === 'string' ? label : route.name}
                </Text>
              </TabPressable>
            );
          })}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  host: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    elevation: 0,
  },
  bar: {
    width: '100%',
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: appTheme.spacing.xs,
  },
  tab: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: appTheme.spacing.xs,
    gap: 2,
  },
  icon_slot: {
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontWeight: '500',
    textAlign: 'center',
  },
  label_focused: {
    fontWeight: '700',
  },
});
